/**
 * Punkt 118 (Antoine, 24.09.2026: „Ja, eigene Seite“): Vertrieb → Angebote — alle Angebote über
 * alle Kunden, eine Zeile je Vorgang mit Angebot. Quelle `GET /api/angebote`: Betrag und Status
 * kommen fertig vom Server (`routes/angebote.ts#angebotStatus`), hier wird nichts gerechnet.
 *
 * Listenstandard wie Buchungen: `useListSort` + `SortierKopf` + `ListFooter`, Reiter per
 * `ListenReiter`, Suche und Reiter in der Adresse (`useUrlZustand`), leerer Reiter nennt Treffer
 * in den anderen (`andereTreffer`). Klick auf die Zeile öffnet den Vorgang mit Herkunft „Angebote“,
 * der Knopf „PDF“ lädt die vorhandene Angebots-PDF (`/api/offers/:id/document.pdf`).
 */
import { useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { useUrlZustand } from "../hooks/useUrlZustand";
import { ladeDateiHerunter } from "../lib/files";
import { oeffneGlobaleSuche } from "../lib/globale-suche";
import { vorgangPfad } from "../lib/pfade";
import { useMitHerkunft } from "../lib/rueckweg";
import { formatDate, money, type StatusMeta } from "../ui/format";
import { datumPruefenNoetig } from "../ui/DatumVergangenHinweis";
import {
  Card,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  KeineAngabe,
  LISTEN_KOPF_ZELLE,
  ListFooter,
  ListenReiter,
  LoadingRows,
  PageHeader,
  SortierKopf,
  StatusPill,
  ZEILEN_AKTION,
  ZeilenLink,
} from "../ui/components";

export type AngebotStatus = "entwurf" | "versendet" | "angesehen" | "angenommen" | "abgelaufen" | "abgelehnt";

export interface AngebotZeile {
  eventId: string;
  number: string | null;
  customerId: string | null;
  kundeName: string | null;
  kundeFirma: string | null;
  anlass: string | null;
  veranstaltungsdatum: string | null;
  veranstaltungsende?: string | null;
  status: string;
  fassung: number;
  angenommeneFassung: number | null;
  versendetAm: string | null;
  gueltigBis: string | null;
  aufrufe: number | null;
  betragCents: number | null;
  mehrdeutigeBetraege: number[] | null;
  pdfOptionId: string | null;
  angebotStatus: AngebotStatus;
}

export const ANGEBOT_STATUS: Record<AngebotStatus, StatusMeta> = {
  entwurf: { label: "Entwurf", tone: "neutral" },
  versendet: { label: "Versendet", tone: "info" },
  angesehen: { label: "Angesehen", tone: "primary" },
  angenommen: { label: "Angenommen", tone: "success" },
  abgelaufen: { label: "Abgelaufen", tone: "warn" },
  abgelehnt: { label: "Abgelehnt", tone: "neutral" },
};
const STATUS_REIHENFOLGE: Record<AngebotStatus, number> = { entwurf: 0, versendet: 1, angesehen: 2, angenommen: 3, abgelaufen: 4, abgelehnt: 5 };

const REITER = [
  { key: "alle", label: "Alle" },
  { key: "offen", label: "Offen" },
  { key: "angenommen", label: "Angenommen" },
  { key: "abgelaufen", label: "Abgelaufen" },
  { key: "entwuerfe", label: "Entwürfe" },
] as const;
type ReiterKey = (typeof REITER)[number]["key"];

/** Eine Quelle für Liste UND Reiter-Zähler. „Abgelehnt“ steht nur unter „Alle“. */
function imReiter(a: AngebotZeile, reiter: ReiterKey): boolean {
  if (reiter === "alle") return true;
  if (reiter === "offen") return a.angebotStatus === "versendet" || a.angebotStatus === "angesehen";
  if (reiter === "entwuerfe") return a.angebotStatus === "entwurf";
  return a.angebotStatus === reiter;
}

const ZELLE = "border-t border-[#F6F0E4] px-[7px] py-3 align-middle first:pl-[18px] last:pr-[18px] dark:border-white/[0.06]";
const NEBEN = "text-[#57503F] dark:text-secondary-fixed-dim";

const SPALTEN: SortSpalte<AngebotZeile>[] = [
  { key: "kunde", label: "Kunde", type: "text", accessor: (a) => a.kundeName ?? a.kundeFirma },
  { key: "anlass", label: "Anlass", type: "text", accessor: (a) => a.anlass ?? a.number },
  { key: "datum", label: "Veranstaltung", type: "date", accessor: (a) => a.veranstaltungsdatum },
  { key: "fassung", label: "Fassung", type: "number", accessor: (a) => (a.fassung > 0 ? a.fassung : null) },
  { key: "betrag", label: "Betrag", type: "money", accessor: (a) => a.mehrdeutigeBetraege?.[0] ?? a.betragCents },
  { key: "versendet", label: "Versendet am", type: "date", accessor: (a) => a.versendetAm },
  { key: "gueltig", label: "Gültig bis", type: "date", accessor: (a) => a.gueltigBis },
  { key: "status", label: "Status", type: "status", accessor: (a) => a.angebotStatus, statusOrder: STATUS_REIHENFOLGE },
];

/**
 * V-1304 (02.11.2026): ein noch offenes Angebot mit Veranstaltungsdatum in der Vergangenheit ist
 * meist ein Tippfehler im Jahr — es bleibt in „Offen“ und trägt sichtbar „Datum prüfen“.
 */
const DATUM_PRUEFEN: StatusMeta = { label: "Datum prüfen", tone: "warn" };
export function datumPruefen(a: Pick<AngebotZeile, "angebotStatus" | "veranstaltungsdatum" | "veranstaltungsende">, jetzt: Date = new Date()): boolean {
  if (a.angebotStatus !== "entwurf" && a.angebotStatus !== "versendet" && a.angebotStatus !== "angesehen") return false;
  return datumPruefenNoetig(a.veranstaltungsdatum, null, jetzt, a.veranstaltungsende);
}

function datum(wert: string | null) {
  return wert ? formatDate(wert) : <KeineAngabe />;
}

export default function AngebotePage() {
  const { data, loading, error, reload } = useApi<AngebotZeile[]>("/api/angebote");
  const alle = useMemo(() => data ?? [], [data]);
  const mitHier = useMitHerkunft();
  const [zustand, setzeZustand] = useUrlZustand({ reiter: "alle", q: "" });
  const reiter: ReiterKey = REITER.some((r) => r.key === zustand.reiter) ? (zustand.reiter as ReiterKey) : "alle";
  const setReiter = (k: ReiterKey) => setzeZustand({ reiter: k });
  const suche = zustand.q;
  const setSuche = (q: string) => setzeZustand({ q });
  const [pdfLaedt, setPdfLaedt] = useState<string | null>(null);
  const [pdfFehler, setPdfFehler] = useState<string | null>(null);

  // Suche wirkt VOR den Reitern — die Reiter zählen die Suchtreffer (wie Buchungen, Punkt 115).
  const suchTreffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return alle;
    return alle.filter((a) => [a.kundeName, a.kundeFirma, a.anlass, a.number].some((t) => (t ?? "").toLowerCase().includes(q)));
  }, [alle, suche]);
  const zaehler = useMemo(
    () => Object.fromEntries(REITER.map((r) => [r.key, suchTreffer.filter((a) => imReiter(a, r.key)).length])) as Record<ReiterKey, number>,
    [suchTreffer],
  );
  const gefiltert = useMemo(() => suchTreffer.filter((a) => imReiter(a, reiter)), [suchTreffer, reiter]);
  const sort = useListSort("angebote", SPALTEN);
  // ponytail: keine Virtualisierung; die API kappt bei 500 Zeilen.
  const sortiert = sort.sortiere(gefiltert);

  async function ladePdf(a: AngebotZeile) {
    if (!a.pdfOptionId) return;
    setPdfLaedt(a.eventId);
    setPdfFehler(null);
    try {
      await ladeDateiHerunter(`/api/offers/${a.pdfOptionId}/document.pdf`, "Angebot.pdf");
    } catch (e) {
      setPdfFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfLaedt(null);
    }
  }

  return (
    <>
      <PageHeader title="Angebote" subtitle="Alle Angebote über alle Kunden — Fassung, Betrag, Versand und Status auf einen Blick." />

      {alle.length > 0 && (
        <div className="mb-4">
          <Input
            type="search"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Angebote durchsuchen (Kunde, Firma, Anlass) …"
            aria-label="Angebote durchsuchen"
            className="w-full max-w-sm"
          />
        </div>
      )}

      {pdfFehler && <ErrorNote title="PDF konnte nicht erstellt werden" message={pdfFehler} />}

      <Card className="overflow-hidden">
        <ListenReiter label="Angebote nach Status" reiter={REITER.map((r) => ({ ...r, anzahl: zaehler[r.key] ?? 0 }))} aktiv={reiter} onWahl={setReiter} />

        {error ? (
          <ErrorNote zentriert title="Angebote konnten nicht geladen werden" message={error} onRetry={reload} />
        ) : !loading && alle.length === 0 ? (
          <EmptyState icon="request_quote" title="Noch keine Angebote" hint="Sobald zu einem Vorgang ein Angebot entsteht, erscheint es hier." />
        ) : !loading && gefiltert.length === 0 ? (
          <EmptyState
            icon={suche.trim() ? "search_off" : "request_quote"}
            title={zaehler.alle === 0 && suche.trim() ? `Kein Angebot passt zu „${suche.trim()}“` : "Keine Angebote in diesem Reiter"}
            hint={zaehler.alle === 0 ? "Die Suche über alle Bereiche findet auch Anfragen ohne Angebot." : "Wechsle den Reiter oder zeige alle Angebote an."}
            andereTreffer={REITER.filter((r) => r.key !== "alle" && r.key !== reiter).map((r) => ({
              label: r.label,
              anzahl: zaehler[r.key] ?? 0,
              onClick: () => setReiter(r.key),
            }))}
            link={[
              ...(reiter !== "alle" && zaehler.alle > 0 ? [{ label: "Alle Angebote anzeigen", onClick: () => setReiter("alle") }] : []),
              ...(suche.trim() ? [{ label: "In allen Bereichen suchen", onClick: () => oeffneGlobaleSuche(suche.trim()) }] : []),
              ...(suche.trim() ? [{ label: "Suche aufheben", onClick: () => setSuche("") }] : []),
            ]}
          />
        ) : (
          <div className="max-h-[max(20rem,calc(100vh-19rem))] overflow-auto" aria-busy={loading}>
            <table className="handy-karten w-full min-w-[1100px] table-fixed border-collapse text-left text-[13px]">
              <colgroup>
                <col />
                <col />
                <col className="w-[120px]" />
                <col className="w-[84px]" />
                <col className="w-[130px]" />
                <col className="w-[124px]" />
                <col className="w-[110px]" />
                <col className="w-[130px]" />
                <col className="w-[76px]" />
              </colgroup>
              <thead>
                <tr>
                  {SPALTEN.map((sp) => (
                    <SortierKopf
                      key={sp.key}
                      label={sp.label}
                      aktiv={sort.sortKey === sp.key ? sort.dir : null}
                      onClick={() => sort.toggle(sp.key)}
                      rechts={sp.key === "fassung" || sp.key === "betrag"}
                    />
                  ))}
                  <th className={LISTEN_KOPF_ZELLE}>
                    <span className="sr-only">PDF</span>
                  </th>
                </tr>
              </thead>
              {loading ? (
                <LoadingRows rows={8} spalten={["text", "text", "text", "text", "text", "text", "text", "pill", "leer"]} />
              ) : (
                <tbody>
                  {sortiert.map((a) => {
                    const name = a.kundeName ?? a.kundeFirma;
                    return (
                      <tr key={a.eventId} className="group relative transition-colors hover:bg-[#FBF6EC] dark:hover:bg-white/[0.03]">
                        <td className={ZELLE}>
                          <ZeilenLink to={mitHier(vorgangPfad(a.eventId), "Angebote")} label={`Vorgang von ${name ?? "unbekannt"} öffnen`} />
                          {name ? (
                            <span title={name} className="block truncate font-semibold text-on-background group-hover:text-primary dark:text-inverse-on-surface">
                              {name}
                            </span>
                          ) : (
                            <KeineAngabe />
                          )}
                          {a.kundeName && a.kundeFirma && <p className="truncate text-[11.5px] text-on-surface-variant/70">{a.kundeFirma}</p>}
                        </td>
                        <td className={`${ZELLE} ${NEBEN}`}>
                          <span className="block truncate" title={a.anlass ?? undefined}>
                            {a.anlass ?? <KeineAngabe />}
                          </span>
                          {a.number && <p className="truncate text-[11.5px] text-on-surface-variant/70">{a.number}</p>}
                        </td>
                        <td className={`${ZELLE} whitespace-nowrap tabular-nums ${NEBEN}`}>
                          {datum(a.veranstaltungsdatum)}
                          {datumPruefen(a) && (
                            <span className="ml-1.5" data-testid="angebot-datum-pruefen">
                              <StatusPill meta={DATUM_PRUEFEN} icon="event_busy" />
                            </span>
                          )}
                        </td>
                        <td className={`${ZELLE} text-right tabular-nums ${NEBEN}`}>{a.fassung > 0 ? `V${a.fassung}` : <KeineAngabe />}</td>
                        <td className={`${ZELLE} text-right font-semibold tabular-nums text-on-background dark:text-inverse-on-surface`}>
                          {a.mehrdeutigeBetraege && a.mehrdeutigeBetraege.length > 0 ? (
                            <div className="flex flex-col items-end gap-0.5">
                              {a.mehrdeutigeBetraege.map((cents, i) => (
                                <span key={i}>{money(cents)}</span>
                              ))}
                              <span className="text-[11.5px] font-normal text-on-surface-variant/70">mehrere Varianten</span>
                            </div>
                          ) : a.betragCents != null ? (
                            money(a.betragCents)
                          ) : (
                            <KeineAngabe />
                          )}
                        </td>
                        <td className={`${ZELLE} whitespace-nowrap tabular-nums ${NEBEN}`}>{datum(a.versendetAm)}</td>
                        <td className={`${ZELLE} whitespace-nowrap tabular-nums ${NEBEN}`}>{datum(a.gueltigBis)}</td>
                        <td className={ZELLE}>
                          <StatusPill meta={ANGEBOT_STATUS[a.angebotStatus]} />
                        </td>
                        <td className={`${ZELLE} text-center`}>
                          <button
                            type="button"
                            disabled={!a.pdfOptionId || pdfLaedt === a.eventId}
                            onClick={() => void ladePdf(a)}
                            aria-label={`Angebot von ${name ?? "unbekannt"} als PDF herunterladen`}
                            title={a.pdfOptionId ? "PDF herunterladen" : "Keine aktive Variante – kein PDF"}
                            className={`${ZEILEN_AKTION} inline-flex h-[30px] items-center gap-1 rounded-lg px-2 text-[12px] font-semibold text-[#6F675A] transition-colors hover:bg-[#F2ECDF] hover:text-[#262119] disabled:cursor-not-allowed disabled:text-[#DCD3C2] disabled:hover:bg-transparent dark:text-[#b8ae9e] dark:hover:bg-white/[0.06] dark:hover:text-inverse-on-surface dark:disabled:text-white/20`}
                          >
                            <Icon name={pdfLaedt === a.eventId ? "hourglass_empty" : "picture_as_pdf"} className="text-[15px]" />
                            PDF
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
        )}
        {!error && (
          <ListFooter
            anzahl={gefiltert.length}
            gesamt={alle.length}
            einheit="Angebote"
            sortiertNach={sort.spalte?.label ?? null}
            richtung={sort.dir}
            onReset={sort.reset}
          />
        )}
      </Card>
    </>
  );
}
